import 'primeflex/primeflex.css';
import 'primeicons/primeicons.css';
import './InstallerPanelTable.css';
import "primereact/resources/themes/lara-light-indigo/theme.css";
import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { ActionType } from '../../stores/action-types';
import { DataTable } from 'primereact/datatable';
import { DataView } from 'primereact/dataview';
import { Column } from 'primereact/column';
import { Tag } from 'primereact/tag';
import { Button } from 'primereact/button';
import { exportDataTableCsv } from '../../helpers/exportCsv';
import { InputText } from 'primereact/inputtext';
import { Tooltip } from 'primereact/tooltip';
import { Toolbar } from 'primereact/toolbar';
import { Dialog } from 'primereact/dialog';
import { Dropdown } from 'primereact/dropdown';
import { FilterMatchMode } from 'primereact/api';
import { MultiSelect } from 'primereact/multiselect';
import Snackbar from '../../../components/Snackbar/Snackbar';
import { connect } from 'react-redux';
import { getSystemsVeryShort, getRouters, getSimcards, updateRouter, closeUpdateRouterSuccessNotification, closeUpdateRouterFailNotification, getAvailableSimcards,
         addRouter, getRouter, closeCreateRouterSuccessNotification, closeCreateRouterFailNotification, deleteRouter } from '../../stores/actions/installerActions';
import { getSystemsNodesShort } from '../../stores/actions/logsActions';
import 'react-quill/dist/quill.snow.css';
import RouterEditDialog from './EditDialogs/RouterEditDialog';
import SimcardEditDialog from './EditDialogs/SimcardEditDialog';
import { headerStyleSuccess, headerStyleFail, emptyHeaderStyle } from '../../helpers/GlobalVariables';

interface RoutersProps {
    mobileView: boolean;
    dbRouters: any;
    dbSimcards: any;
    currentRouter: any;
    updateRouterSuccess: boolean;
    updateRouterFail: boolean;
    createRouterSuccess: boolean;
    createRouterFail: boolean;
    updateSimcardSuccess: boolean;
    updateSimcardFail: boolean;
    getSystemsVeryShort: () => void;
    getSystemsNodesShort: () => void;
    getAvailableSimcards: () => void;
    getRouters: () => void;
    getSimcards: () => void;
    getRouter: (routerId) => void;
    deleteRouter: (systemId, simcardId, simcardName) => void;
    closeUpdateRouterSuccessNotification: () => void;
    closeUpdateRouterFailNotification: () => void;
    closeCreateRouterSuccessNotification: () => void;
    closeCreateRouterFailNotification: () => void;
}

const Routers = ({
    mobileView,
    dbRouters,
    dbSimcards,
    currentRouter,
    getRouters,
    getSimcards,
    getSystemsNodesShort,
    getAvailableSimcards,
    getRouter,
    deleteRouter,
    updateRouterSuccess,
    updateRouterFail,
    createRouterSuccess,
    createRouterFail,
    updateSimcardSuccess,
    updateSimcardFail,
    closeUpdateRouterSuccessNotification,
    closeUpdateRouterFailNotification,
    closeCreateRouterSuccessNotification,
    closeCreateRouterFailNotification
}: RoutersProps) => {

    let emptyRouter = {
        ID: null,
        systemId: null,
        systemName: null,
        type: 'other',
        firmware: null,
        serialNumber: null,
        purchaseDate: formatDateRemoveTime(new Date()),
        owner: 'WISENE',
        simcardId: null,
        simcardName: null,
        notes: null,
        remarks: null,
        updatedAt: null,
        supplier: 'WISENE',
        installationDate: formatDateRemoveTime(new Date()),
        upsType: null,
        upsSerialNumber: null,
        upsInstallationDate: formatDateRemoveTime(new Date()),
        routerImageData: null,
        routerImageDataMime: null,
        upsImageData: null,
        upsImageDataMime: null,
        antennaImageData: null,
        antennaImageDataMime: null,
        routerConnectionImageData: null,
        routerConnectionImageDataMime: null,
        modemRestarter: false,
        nodeId: null,
        nodeName: null,
        previousNodeId: null,
        previousNodeName: null,
        previousSimcardId: null,
        previousSimcardName: null,
        previousSystemName: null
    };

    const closeNotificationTimer = useRef(null);
    const dispatch = useDispatch();

    const [routerDialog, setRouterDialog] = useState(false);
    const [selectedRouter, setSelectedRouter] = useState(null);
    const [router, setRouter] = useState(emptyRouter);
    const [deleteRouterDialog, setDeleteRouterDialog] = useState(false);
    const [routers, setRouters] = useState(dbRouters);
    const [editSimcardId, setEditSimcardId] = useState(null);

    const [mobileFilters, setMobileFilters] = useState({
        systemName: '',
        nodeName: '',
        type: null,
        serialNumber: '',
        firmware: '',
        owner: null,
        supplier: null,
        simcardName: '',
    });
    const [mobileSortField, setMobileSortField] = useState('ID');
    const [mobileSortOrder, setMobileSortOrder] = useState<-1 | 1>(-1);
    const [mobileFilterDialog, setMobileFilterDialog] = useState(false);
    const [pendingFilters, setPendingFilters] = useState(mobileFilters);
    const [pendingSortField, setPendingSortField] = useState(mobileSortField);
    const [pendingSortOrder, setPendingSortOrder] = useState(mobileSortOrder);
    
    const closeNotification = useCallback(() => {
        dispatch({ type: ActionType.CLOSE_UPDATE_SIMCARD_SUCCESS_NOTIFICATION });
        dispatch({ type: ActionType.CLOSE_UPDATE_SIMCARD_FAIL_NOTIFICATION });
    }, [dispatch]);


    const openMobileFilterDialog = () => {
        setPendingFilters(mobileFilters);
        setPendingSortField(mobileSortField);
        setPendingSortOrder(mobileSortOrder);
        setMobileFilterDialog(true);
    };

    const applyMobileFilters = () => {
        setMobileFilters(pendingFilters);
        setMobileSortField(pendingSortField);
        setMobileSortOrder(pendingSortOrder);
        setMobileFilterDialog(false);
    };

    const resetMobileFilters = () => {
        const empty = {
            systemName: '', nodeName: '', type: null, serialNumber: '',
            firmware: '', owner: null, supplier: null, simcardName: '',
        };
        setPendingFilters(empty);
        setPendingSortField('ID');
        setPendingSortOrder(-1);
    };

    // Filtered + sorted list for DataView
    const filteredRouters = useMemo(() => {
        if (!routers) return [];
        let result = [...routers];

        if (mobileFilters.systemName)
            result = result.filter(r => r.systemName?.toLowerCase().includes(mobileFilters.systemName.toLowerCase()));
        if (mobileFilters.nodeName)
            result = result.filter(r => r.nodeName?.toLowerCase().includes(mobileFilters.nodeName.toLowerCase()));
        if (mobileFilters.type?.length)
            result = result.filter(r => mobileFilters.type.includes(r.type));
        if (mobileFilters.serialNumber)
            result = result.filter(r => r.serialNumber?.toLowerCase().includes(mobileFilters.serialNumber.toLowerCase()));
        if (mobileFilters.firmware)
            result = result.filter(r => r.firmware?.toLowerCase().includes(mobileFilters.firmware.toLowerCase()));
        if (mobileFilters.owner)
            result = result.filter(r => r.owner === mobileFilters.owner);
        if (mobileFilters.supplier)
            result = result.filter(r => r.supplier === mobileFilters.supplier);
        if (mobileFilters.simcardName)
            result = result.filter(r => r.simcardName?.toLowerCase().includes(mobileFilters.simcardName.toLowerCase()));

        result.sort((a, b) => {
            const aVal = a[mobileSortField] ?? '';
            const bVal = b[mobileSortField] ?? '';
            if (aVal < bVal) return -1 * mobileSortOrder;
            if (aVal > bVal) return 1 * mobileSortOrder;
            return 0;
        });

        return result;
    }, [routers, mobileFilters, mobileSortField, mobileSortOrder]);

    const activeFilterCount = useMemo(() => {
        return Object.values(mobileFilters).filter(v => v !== '' && v !== null && !(Array.isArray(v) && v.length === 0)).length;
    }, [mobileFilters]);

    const sortOptions = [
        { label: 'ID', value: 'ID' },
        { label: 'System', value: 'systemName' },
        { label: 'Node name', value: 'nodeName' },
        { label: 'Type', value: 'type' },
        { label: 'Serial Number', value: 'serialNumber' },
        { label: 'Firmware', value: 'firmware' },
        { label: 'Purchase Date', value: 'purchaseDate' },
        { label: 'Owner', value: 'owner' },
        { label: 'Supplier', value: 'supplier' },
        { label: 'SIM Card', value: 'simcardName' },
        { label: 'Updated At', value: 'updatedAt' },
    ];


    const selectedSimcardForEdit = useMemo(() =>
        dbSimcards?.find(s => s.ID === editSimcardId) ?? null,
    [editSimcardId, dbSimcards]);
    const enumFields = ["owner", "supplier"];

    const openNew = () => { setSelectedRouter(null); setRouterDialog(true); };
    const editRouter = (router) => { setSelectedRouter(router); setRouterDialog(true); };


    const createFilters = () => {
        let base = {
            global: { value: null, matchMode: FilterMatchMode.CONTAINS },
            systemName: { value: null, matchMode: FilterMatchMode.CONTAINS },
            nodeName: { value: null, matchMode: FilterMatchMode.CONTAINS },
            serialNumber: { value: null, matchMode: FilterMatchMode.CONTAINS },
            firmware: { value: null, matchMode: FilterMatchMode.CONTAINS },
            simcardName: { value: null, matchMode: FilterMatchMode.CONTAINS },
            type: { value: null, matchMode: FilterMatchMode.IN },
        };

        enumFields.forEach(field => {
            base[field] = { value: null, matchMode: FilterMatchMode.EQUALS };
        });

        return base;
    };

    const [filters, setFilters] = useState(createFilters());

    useEffect(() => {
        closeNotification();
        getSystemsNodesShort();
        getRouters();
        getSimcards();
        getAvailableSimcards();
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (dbRouters) {
            setRouters(dbRouters);
        }

    }, [dbRouters]);

    useEffect(() => {
        if (updateSimcardFail || updateSimcardSuccess) {
            clearTimeout(closeNotificationTimer.current);
            closeNotificationTimer.current = setTimeout(closeNotification, 6000);
        }
        return () => clearTimeout(closeNotificationTimer.current);
    }, [updateSimcardFail, updateSimcardSuccess, closeNotification]);

    useEffect(() => {
        if (updateSimcardSuccess) {
            getRouters();
        }
    }, [updateSimcardSuccess]);

    useEffect(() => {
        if (updateRouterSuccess) {
            setTimeout(() => {
                closeUpdateRouterSuccessNotification();
            }, 6000);
            setRouterDialog(false);
        }
    }, [updateRouterSuccess]);

    useEffect(() => {
        if (updateRouterSuccess || createRouterSuccess) {
            getAvailableSimcards();
        }
    }, [updateRouterSuccess, createRouterSuccess, ]);

    useEffect(() => {
        if (updateRouterFail) {
            setTimeout(() => {
                closeUpdateRouterFailNotification();
            }, 6000)
        }
    }, [updateRouterFail]);


    useEffect(() => {
        if (createRouterSuccess) {
            setTimeout(() => {
                closeCreateRouterSuccessNotification();
            }, 6000);
            setRouterDialog(false);
        }
    }, [createRouterSuccess]);

    useEffect(() => {
        if (createRouterFail) {
            setTimeout(() => {
                closeCreateRouterFailNotification();
            }, 6000)
        }
    }, [createRouterFail]);

    useEffect(() => {
        if (currentRouter) {
            let _router = { ...router };
            _router.routerImageData = currentRouter.routerImageData;
            _router.routerImageDataMime = currentRouter.routerImageDataMime;
            _router.upsImageData = currentRouter.upsImageData;
            _router.upsImageDataMime = currentRouter.upsImageDataMime;
            _router.antennaImageData = currentRouter.antennaImageData;
            _router.antennaImageDataMime = currentRouter.antennaImageDataMime;
            _router.routerConnectionImageData = currentRouter.routerConnectionImageData;
            _router.routerConnectionImageDataMime = currentRouter.routerConnectionImageDataMime;
            _router.previousNodeId = _router.nodeId;
            _router.previousNodeName = _router.nodeName;
            _router.previousSimcardId = _router.simcardId;
            _router.previousSimcardName = _router.simcardName;
            _router.previousSystemName = _router.systemName;
            setRouter(_router);
        }
    }, [currentRouter]);
    const dt = useRef(null);

    useEffect(() => {
        if (routerDialog && router.ID) {
            getRouter(router.ID);
        }
    }, [routerDialog, router.ID]);

    const owners = ['customer', 'WISENE'];
    const suppliers = ['customer', 'WISENE'];
    const routerTypes = [
        'MOXA OnCell G3151-HSPA', 'MOXA OnCell 3120-LTE-1 HW Rev. 1.0.0',
        'MOXA OnCell 3120-LTE-1 HW Rev. 2.0.0', 'ADVANTECH ICR-2431',
        'ADVANTECH ICR-2031', 'other', 'FRITZ!Box 6890 LTE'
    ];
    const upsTypes = [
        'Router UPS-15', 'Router UPS-30', 'Router UPS-30-PoE',
        'Router UPS-15-PoE', 'ZAS-24.1.1'
    ];
    const enumFilter = (field, options) => (
        <Dropdown
            value={filters[field]?.value}
            options={options}
            onChange={(e) =>
            setFilters((prev) => ({
                ...prev,
                [field]: { value: e.value, matchMode: FilterMatchMode.EQUALS },
            }))
            }
            placeholder={'-'}
            showClear
            className="w-full"
        />
    );

    const hideDeleteRouterDialog = () => {
        setDeleteRouterDialog(false);
    };

    const confirmDeleteRouter = (router) => {
        setRouter(router);
        setDeleteRouterDialog(true);
    };

    const deleteRouterItem = () => {
        deleteRouter(router.ID, router.simcardId, router.simcardName);
        let _routers = routers.filter((val) => val.ID !== router.ID);
        setRouters(_routers);
        setDeleteRouterDialog(false);
        setRouter(emptyRouter);
    };

    function formatDateRemoveTime(date) {
        return date.getFullYear() +  "-" + String(date.getMonth() + 1).padStart(2, "0") + "-" + String(date.getDate()).padStart(2, "0");
    }

    const purchaseDateTemplate = (rowData) => {
        if (!rowData.purchaseDate) return null;

        const dateOnly = rowData.purchaseDate.substring(0, 10);
        const [year, month, day] = dateOnly.split("-");

        return `${year}-${month}-${day}`;
    };

    const simcardTemplate = (rowData) => {
        if (!rowData.simcardId) return null;
        return (
            <div className="flex align-items-center gap-2">
                <span>{rowData.simcardName}</span>
                <Button
                    icon="pi pi-pencil"
                    rounded text size="small"
                    onClick={() => setEditSimcardId(rowData.simcardId)}
                />
            </div>
        );
    };

    const typeTemplate = (rowData) => {
        if (rowData.type) {
            const tooltipId = `type-row-${rowData.ID}`;
            const shortTypeNames = {
                'MOXA OnCell G3151-HSPA': 'MOXA G3151',
                'MOXA OnCell 3120-LTE-1 HW Rev. 1.0.0': 'MOXA 3120 1.0.0',
                'MOXA OnCell 3120-LTE-1 HW Rev. 2.0.0': 'MOXA 3120 2.0.0',
                'ADVANTECH ICR-2431': 'ADVANTECH ICR-2431',
                'ADVANTECH ICR-2031': 'ADVANTECH ICR-2031',
                'other': 'other',
                'FRITZ!Box 6890 LTE': 'FRITZ!Box'
            };

            const typeColors = {
                'MOXA OnCell G3151-HSPA': 'bg-purple-500 text-white',
                'MOXA OnCell 3120-LTE-1 HW Rev. 1.0.0': 'bg-pink-500 text-white',
                'MOXA OnCell 3120-LTE-1 HW Rev. 2.0.0': 'bg-emerald-500 text-white',
                'ADVANTECH ICR-2431': 'bg-yellow-500 text-black',
                'ADVANTECH ICR-2031': 'bg-orange-500 text-white',
                'FRITZ!Box 6890 LTE': 'bg-blue-400 text-white',
                'other': 'bg-gray-400 text-black'
            };
            return <>
                <Tooltip target={`#${tooltipId}`} />
                <Tag 
                    id={tooltipId}
                    value={shortTypeNames[rowData.type]} 
                    className={`type-tag ${typeColors[rowData.type]} px-2 py-1 rounded`} 
                    data-pr-tooltip={rowData.type} 
                />
            </> ;
        } 
        return null;
    };

    const typeFilterTemplate = (options) => {
        const types = [
            { label: 'MOXA G3151', value: 'MOXA OnCell G3151-HSPA' },
            { label: 'MOXA 3120 1.0.0', value: 'MOXA OnCell 3120-LTE-1 HW Rev. 1.0.0' },
            { label: 'MOXA 3120 2.0.0', value: 'MOXA OnCell 3120-LTE-1 HW Rev. 2.0.0' },
            { label: 'ADVANTECH ICR-2431', value: 'ADVANTECH ICR-2431' },
            { label: 'ADVANTECH ICR-2031', value: 'ADVANTECH ICR-2031' },
            { label: 'other', value: 'other' },
            { label: 'FRITZ!Box', value: 'FRITZ!Box 6890 LTE' }
        ];
        const typeColors = {
            'MOXA OnCell G3151-HSPA': 'bg-purple-500 text-white',
            'MOXA OnCell 3120-LTE-1 HW Rev. 1.0.0': 'bg-pink-500 text-white',
            'MOXA OnCell 3120-LTE-1 HW Rev. 2.0.0': 'bg-emerald-500 text-white',
            'ADVANTECH ICR-2431': 'bg-yellow-500 text-black',
            'ADVANTECH ICR-2031': 'bg-orange-500 text-white',
            'FRITZ!Box 6890 LTE': 'bg-blue-400 text-white',
            'other': 'bg-gray-400 text-black'
        };
        return (
            <MultiSelect
                value={options.value}
                options={types}
                optionLabel="label"
                optionValue="value"
                onChange={(e) => options.filterApplyCallback(e.value)}
                placeholder="Any"
                className="p-column-filter"
                maxSelectedLabels={1}
                style={{ minWidth: '14rem' }}
                itemTemplate={(option) => (
                    <Tag 
                        value={option.label}
                        className={`${typeColors[option.value]} px-2 py-1 rounded`} 
                    />
                )}
            />
        );
    };

    const deletedNodeWarningIcon = (rowData) => {
        if (!rowData.nodeDeleted) return null;
        const tooltipId = `nodeDeletedWarning-${rowData.ID}`;
        return (
            <>
                <Tooltip target={`#${tooltipId}`} />
                <i
                    id={tooltipId}
                    className="pi pi-exclamation-triangle"
                    style={{ color: '#c55', marginLeft: '0.4rem', flexShrink: 0 }}
                    data-pr-tooltip="Node or System is deleted"
                    data-pr-position="top"
                />
            </>
        );
    };

    const systemNameTemplate = (rowData) => {
        if (rowData.systemId) {
            const tooltipId = `systemName-${rowData.ID}`;
            return (
                <span style={{ display: 'inline-flex', alignItems: 'center', maxWidth: '100%' }}>
                    <Tooltip target={`#${tooltipId}`} />
                    <span
                        id={tooltipId}
                        className="systemName-nodeName"
                        data-pr-tooltip={rowData.systemName}
                        style={{
                            display: 'inline-block',
                            maxWidth: '100%',
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis'
                        }}
                    >
                        {rowData.systemName}
                    </span>
                    {deletedNodeWarningIcon(rowData)}
                </span>
            );
        }
        return null;
    };

    const nodeNameTemplate = (rowData) => {
        if (rowData.nodeId) {
            const tooltipId = `nodeName-${rowData.ID}`;
            return (
                <span style={{ display: 'inline-flex', alignItems: 'center', maxWidth: '100%' }}>
                    <Tooltip target={`#${tooltipId}`} />
                    <span
                        id={tooltipId}
                        className="systemName-nodeName"
                        data-pr-tooltip={rowData.nodeName}
                        style={{
                            display: 'inline-block',
                            maxWidth: '100%',
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis'
                        }}
                    >
                        {rowData.nodeName}
                    </span>
                    {deletedNodeWarningIcon(rowData)}
                </span>
            );
        }
        return null;
    };


    const updatedAtTemplate = (rowData) => {
        if (rowData.updatedAt) {
            return (new Date(rowData.updatedAt)).toLocaleString('sv-SE').replace('T', ' ');
        } 
        return null;
    };


    const cardTemplate = (rowData) => {
        return (
            <div className="col-12">
                {renderMobileNotifications()}
                <div className="border-1 surface-border border-round p-3 mb-2 surface-card">

                    <div className="flex justify-content-between align-items-center pb-2 mb-3 border-bottom-1 surface-border">
                        <span className="font-bold text-lg">{rowData.ID}</span>
                    </div>

                    <div className="mb-2 p-2 surface-50 border-round">
                        <div className="grid">
                            <div className="col-5">
                                <div className="text-xs text-color-secondary">System</div>
                                <div className="font-medium">{rowData.systemName || '---'}</div>
                            </div>
                            <div className="col-2"></div>
                            <div className="col-5">
                                <div className="text-xs text-color-secondary">Node name</div>
                                <div className="font-medium">{rowData.nodeName || '---'}</div>
                            </div>
                        </div>
                        <div className="grid">
                            <div className="col-5">
                                <div className="text-xs text-color-secondary">Type</div>
                                <div className="font-medium">{typeTemplate(rowData)}</div>
                            </div>
                            <div className="col-2"></div>
                            <div className="col-5">
                                <div className="text-xs text-color-secondary">Serial Number</div>
                                <div className="font-medium">{rowData.serialNumber || '---'}</div>
                            </div>
                        </div>
                        <div className="col-5">
                                <div className="text-xs text-color-secondary">Firmware</div>
                                <div className="font-medium">{rowData.firmware || '---'}</div>
                        </div>
                    </div>

                    <div className="mb-2 p-2 surface-50 border-round">
                        <div className="grid">
                            <div className="col-5">
                                <div className="text-xs text-color-secondary">Purchase Date</div>
                                <div className="font-medium">{purchaseDateTemplate(rowData)}</div>
                            </div>
                            <div className="col-2"></div>
                            <div className="col-5">
                                <div className="text-xs text-color-secondary">Owner</div>
                                <div className="font-medium">{rowData.owner || '---'}</div>
                            </div>
                        </div>
                        <div className="grid">
                            <div className="col-5">
                                <div className="text-xs text-color-secondary">Supplier</div>
                                <div className="font-medium">{rowData.supplier || '---'}</div>
                            </div>
                        </div>
                    </div>

                    <div className="mb-3 p-2 surface-50 border-round">
                        <div className="grid">
                            <div className="col-5">
                                <div className="text-xs text-color-secondary">SIM Card</div>
                                <div className="font-medium">{simcardTemplate(rowData) || '---'}</div>
                            </div>
                            <div className="col-2"></div>
                            <div className="col-5">
                                <div className="text-xs text-color-secondary">Modem Restarter</div>
                                <div className="font-medium">
                                    <Tag
                                        value={rowData.modemRestarter ? 'Yes' : 'No'}
                                        className={`px-2 py-1 rounded ${rowData.modemRestarter ? 'bg-green-500 text-white' : 'bg-red-500 text-white'}`}
                                    />
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="flex justify-content-between align-items-center pt-2 border-top-1 surface-border">
                        <div className="flex flex-column">
                            <span className="text-xs text-color-secondary">Updated At</span>
                            <span className="font-medium">{updatedAtTemplate(rowData)}</span>
                        </div>
                        <div>
                            {actionBodyTemplate(rowData)}
                        </div>
                    </div>

                </div>
            </div>
        );
    };

    const mobileFilterBar = (
        <div className="flex align-items-center gap-2 mb-3">
            <span className="p-input-icon-left flex-1">
                {/* <i className="pi pi-search" /> */}
                <InputText
                    value={mobileFilters.systemName}
                    onChange={(e) => setMobileFilters(prev => ({ ...prev, systemName: e.target.value }))}
                    placeholder="Search system..."
                    className="w-full"
                />
            </span>
            <div className="relative">
                <Button
                    icon="pi pi-filter"
                    rounded
                    outlined={activeFilterCount === 0}
                    severity={activeFilterCount > 0 ? 'info' : undefined}
                    onClick={openMobileFilterDialog}
                />
                {activeFilterCount > 0 && (
                    <span className="absolute top-0 right-0 bg-primary text-white border-circle flex align-items-center justify-content-center"
                        style={{ width: '1.2rem', height: '1.2rem', fontSize: '0.65rem', transform: 'translate(30%, -30%)' }}>
                        {activeFilterCount}
                    </span>
                )}
            </div>
            <Button
                icon="pi pi-plus"
                severity="success"
                rounded
                onClick={openNew}
            />
        </div>
    );

    const mobileFilterDialogFooter = (
        <div className="flex justify-content-between">
            <Button label="Reset" icon="pi pi-times" text severity="secondary" onClick={resetMobileFilters} />
            <div className="flex gap-2">
                <Button label="Cancel" icon="pi pi-times" outlined onClick={() => setMobileFilterDialog(false)} />
                <Button label="Apply" icon="pi pi-check" onClick={applyMobileFilters} />
            </div>
        </div>
    );
    const actionBodyTemplate = (rowData) => {
        return (
            <React.Fragment>
                <Button icon="pi pi-pencil" rounded outlined className="mr-2" onClick={() => editRouter(rowData)} />
                <Button icon="pi pi-trash" rounded outlined severity="danger" onClick={() => confirmDeleteRouter(rowData)} />
            </React.Fragment>
        );
    };

    const rightToolbarTemplate = () => {
        return (
            <div className="flex flex-wrap gap-2">
                {/* <Button icon="pi pi-refresh" rounded raised onClick={()=>getRouters()} /> */}
                <Button label="Export" icon="pi pi-file-export" outlined onClick={() => exportDataTableCsv(dt, 'routers')} />
            </div>
        );
    };

    const leftToolbarTemplate = () => {
        return (
            <div className="flex flex-wrap gap-2">
                <Button label="New" icon="pi pi-plus" severity="success" onClick={openNew} />
            </div>
        );
    };

    const deleteRouterDialogFooter = (
        <React.Fragment>
            <Button label="No" icon="pi pi-times" outlined onClick={hideDeleteRouterDialog} />
            <Button label="Yes" icon="pi pi-check" severity="danger" onClick={deleteRouterItem} />
        </React.Fragment>
    );

    const dataView = useMemo(() => {
        return (
            <div>
                {mobileFilterBar}
                <DataView
                    value={filteredRouters}
                    itemTemplate={cardTemplate}
                    paginator
                    rows={10}
                />
            </div>
        );
    }, [filteredRouters, mobileFilters, mobileFilterDialog, createRouterSuccess, updateRouterSuccess, updateSimcardFail, updateSimcardSuccess]);

    const dataTable = useMemo(() => {
        return (
            <div className="card">
                <Toolbar className="mb-4" left={leftToolbarTemplate}  right={rightToolbarTemplate}> </Toolbar>

                <DataTable ref={dt} value={routers} sortField="ID" sortOrder={-1} responsiveLayout="stack" scrollable
                        dataKey="ID"  paginator rows={25} rowsPerPageOptions={[10, 25, 50, 100]}
                        paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
                        currentPageReportTemplate="Showing {first} to {last} of {totalRecords} items"
                        filters={filters} onFilter={(e) => setFilters(e.filters)} filterDisplay="row" stripedRows size='small' resizableColumns
                        exportFilename="routers" >
                <Column exportable={false}></Column>
                <Column                    
                    field="ID" 
                    header="ID"
                    style={{maxWidth: '45px'}}
                    sortable>
                </Column>
                <Column 
                    field="systemName" 
                    header="System"
                    body={systemNameTemplate}
                    style={{ maxWidth: '180px' }}
                    sortable
                    filter 
                    filterPlaceholder=""
                    showFilterMenu={false}>
                </Column>
                <Column 
                    field="nodeName"
                    header="Node name"
                    body={nodeNameTemplate}
                    style={{ maxWidth: '180px' }}
                    sortable
                    filter 
                    filterPlaceholder=""
                    showFilterMenu={false}>
                </Column>
                <Column 
                    field="type" 
                    header="Type"
                    body={typeTemplate}
                    style={{ maxWidth: '180px'}}
                    sortable
                    filter 
                    filterMatchMode="in"
                    showFilterMenu={false}
                    filterElement={typeFilterTemplate}
                    > 
                </Column>
                <Column 
                    field="serialNumber" 
                    header="Serial Number"
                    sortable
                    filter 
                    filterPlaceholder=""
                    showFilterMenu={false}>
                </Column>
                <Column 
                    field="firmware" 
                    header="Firmware"
                    sortable
                    filter 
                    filterPlaceholder=""
                    showFilterMenu={false}>
                </Column>
                <Column 
                    field="purchaseDate" 
                    header="Purchase date"
                    body={purchaseDateTemplate}
                    sortable>               
                </Column>
                <Column 
                    field="owner" 
                    header="Owner"
                    style={{maxWidth:'100px'}}
                    sortable
                    filter 
                    showFilterMenu={false}
                    filterElement={enumFilter('owner', owners)}
                    > 
                </Column>
                <Column 
                    field="supplier" 
                    header="Supplier"
                    sortable
                    filter 
                    filterPlaceholder=""
                    showFilterMenu={false}
                    filterElement={enumFilter('supplier', suppliers)}>             
                </Column>
                <Column 
                    field="simcardName" 
                    header="SIM card"
                    sortable
                    body={simcardTemplate}
                    filter 
                    filterPlaceholder=""
                    showFilterMenu={false}>     
                </Column>
                <Column 
                    field="modemRestarter" 
                    header="MR"
                    style={{maxWidth:'60px'}}
                    body={(rowData) =>
                         <Tag 
                            value={rowData.modemRestarter ? 'Yes' : 'No'} 
                            className={`px-2 py-1 rounded ${ rowData.modemRestarter ? 'bg-green-500 text-white' : 'bg-red-500 text-white'}`} 
                        />
                    }
                    sortable>                
                </Column>
                <Column 
                    field="updatedAt" 
                    header="Updated at"
                    body={updatedAtTemplate}
                    sortable>                
                </Column>
                <Column body={actionBodyTemplate} exportable={false} style={{ minWidth: '12rem', maxWidth: '24rem' }}></Column>
                </DataTable>
            </div>
        );
    }, [routers, filters, createRouterSuccess, updateRouterSuccess]);


    const renderMobileNotifications = (): JSX.Element => {
        if (!mobileView) return null;
        const failed = updateSimcardFail;
        const succeeded = updateSimcardSuccess;
        if (failed || succeeded) {
            const headerStyle = failed ? headerStyleFail : headerStyleSuccess;
            const message = failed
                ? 'Saving SIM card failed.'
                : 'SIM card was saved successfully.';
            return (
                <h5
                    className={updateRouterFail ? 'notification-open-bind-device-fail' : 'notification-open-bind-device-success'}
                    style={headerStyle}
                >
                    {message}
                </h5>
            );
        }
        return <div style={emptyHeaderStyle}></div>;
    };

    return (
        <div>
            { !!dbRouters && ( mobileView ? dataView : dataTable ) }

            <RouterEditDialog
                visible={routerDialog}
                initialRouter={selectedRouter}
                onHide={() => setRouterDialog(false)}
            />

            <Dialog visible={deleteRouterDialog} style={{ width: '32rem' }} breakpoints={{ '960px': '75vw', '641px': '90vw' }} header="Confirm" modal footer={deleteRouterDialogFooter} onHide={hideDeleteRouterDialog}>
                <div className="confirmation-content">
                    <i className="pi pi-exclamation-triangle mr-3" style={{ fontSize: '2rem' }} />
                    {router && (
                        <span>
                            Are you sure you want to delete this item (#{router.ID})?
                        </span>
                    )}
                </div>
            </Dialog>

            <SimcardEditDialog
                key={editSimcardId ?? 'none'}
                visible={!!editSimcardId}
                initialSimcard={selectedSimcardForEdit}
                onHide={() => setEditSimcardId(null)}
            />

            <Dialog
                visible={mobileFilterDialog}
                onHide={() => setMobileFilterDialog(false)}
                header="Filter &amp; Sort"
                style={{ width: '95vw' }}
                footer={mobileFilterDialogFooter}
                modal
                className="p-fluid"
            >
                {/* Sort */}
                <div className="mb-4">
                    <label className="font-bold mb-2 block">Sort by</label>
                    <div className="flex gap-2">
                        <Dropdown
                            value={pendingSortField}
                            options={sortOptions}
                            onChange={(e) => setPendingSortField(e.value)}
                            className="flex-1"
                        />
                        <Button
                            icon={pendingSortOrder === 1 ? 'pi pi-sort-amount-up' : 'pi pi-sort-amount-down'}
                            outlined
                            onClick={() => setPendingSortOrder(prev => prev === 1 ? -1 : 1)}
                            tooltip={pendingSortOrder === 1 ? 'Ascending' : 'Descending'}
                        />
                    </div>
                </div>
                <div className="mb-3">
                    <label className="text-sm text-color-secondary mb-1 block">Node name</label>
                    <InputText value={pendingFilters.nodeName}
                        onChange={(e) => setPendingFilters(prev => ({ ...prev, nodeName: e.target.value }))}
                        placeholder="Search..." />
                </div>
                <div className="mb-3">
                    <label className="text-sm text-color-secondary mb-1 block">Type</label>
                    <MultiSelect
                        value={pendingFilters.type}
                        options={routerTypes.map(t => ({ label: t, value: t }))}
                        onChange={(e) => setPendingFilters(prev => ({ ...prev, type: e.value }))}
                        placeholder="Any"
                        className="w-full"
                        maxSelectedLabels={1}
                    />
                </div>
                <div className="mb-3">
                    <label className="text-sm text-color-secondary mb-1 block">Serial Number</label>
                    <InputText value={pendingFilters.serialNumber}
                        onChange={(e) => setPendingFilters(prev => ({ ...prev, serialNumber: e.target.value }))}
                        placeholder="Search..." />
                </div>
                <div className="mb-3">
                    <label className="text-sm text-color-secondary mb-1 block">Firmware</label>
                    <InputText value={pendingFilters.firmware}
                        onChange={(e) => setPendingFilters(prev => ({ ...prev, firmware: e.target.value }))}
                        placeholder="Search..." />
                </div>
                <div className="mb-3">
                    <label className="text-sm text-color-secondary mb-1 block">Owner</label>
                    <Dropdown value={pendingFilters.owner} options={owners}
                        onChange={(e) => setPendingFilters(prev => ({ ...prev, owner: e.value }))}
                        placeholder="Any" showClear className="w-full" />
                </div>
                <div className="mb-3">
                    <label className="text-sm text-color-secondary mb-1 block">Supplier</label>
                    <Dropdown value={pendingFilters.supplier} options={suppliers}
                        onChange={(e) => setPendingFilters(prev => ({ ...prev, supplier: e.value }))}
                        placeholder="Any" showClear className="w-full" />
                </div>
                <div className="mb-3">
                    <label className="text-sm text-color-secondary mb-1 block">SIM Card</label>
                    <InputText value={pendingFilters.simcardName}
                        onChange={(e) => setPendingFilters(prev => ({ ...prev, simcardName: e.target.value }))}
                        placeholder="Search..." />
                </div>
            </Dialog>

            <Snackbar
                close
                closeNotification={() => closeUpdateRouterSuccessNotification()}
                color='success'
                message={'Router updated'}
                open={!mobileView && updateRouterSuccess}
                place='tc'
            />
            <Snackbar
                close
                closeNotification={() => closeUpdateRouterFailNotification()}
                color='danger'
                message={'Update error'}
                open={!mobileView && updateRouterFail}
                place='tc'
            />
            <Snackbar
                close
                closeNotification={() => closeCreateRouterSuccessNotification()}
                color='success'
                message={'Router created'}
                open={!mobileView && createRouterSuccess}
                place='tc'
            />
            <Snackbar
                close
                closeNotification={() => closeCreateRouterFailNotification()}
                color='danger'
                message={'Create error'}
                open={!mobileView && createRouterFail}
                place='tc'
            />
            <Snackbar
                close
                closeNotification={closeNotification}
                color={updateSimcardFail ? 'danger' : 'success'}
                message={updateSimcardFail
                    ? 'Saving SIM card failed.'
                    : 'SIM card was saved successfully.'
                }
                open={!mobileView && (updateSimcardFail || updateSimcardSuccess)}
                place='tc'
            />
        </div>
    );
}


const mapStateToProps = (state) => {
    return {
        systemsNodesShort: state.dashboard.systemsNodesShort,
        mobileView: state.dashboard.mobileView,
        dbRouters: state.dashboard.routers,
        dbSimcards: state.dashboard.simcards,
        currentRouter: state.dashboard.currentRouter,
        updateRouterSuccess: state.dashboard.updateRouterSuccess,
        updateRouterFail: state.dashboard.updateRouterFail,
        createRouterSuccess: state.dashboard.createRouterSuccess,
        createRouterFail: state.dashboard.createRouterFail,
        updateSimcardSuccess: state.dashboard.updateSimcardSuccess,
        updateSimcardFail: state.dashboard.updateSimcardFail,
    };
};

const actions = {
    getSystemsVeryShort,
    getSystemsNodesShort,
    getAvailableSimcards,
    getRouters,
    getSimcards,
    updateRouter,
    addRouter,
    getRouter,
    deleteRouter,
    closeUpdateRouterSuccessNotification,
    closeUpdateRouterFailNotification,
    closeCreateRouterSuccessNotification,
    closeCreateRouterFailNotification
};

export default connect(mapStateToProps, actions)(Routers);
